// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL-3.0 section 7(b) apply — see ADDITIONAL-TERMS.md.

// Main-thread controllers around the two workers. The denoise worker runs the
// RNNoise loop; the encode worker turns the mixed signal into a WAV/MP3 blob.

import type {
  DenoiseResult,
  DenoiseWorkerMessage,
  EncodeWorkerMessage,
  OutputFormat,
  PreparedAudio,
} from './types';

export class Denoiser {
  private worker: Worker | null = null;

  /** Run the prepared audio through RNNoise. Rejects on worker error. */
  denoise(
    prepared: PreparedAudio,
    onProgress: (fraction: number) => void,
  ): Promise<DenoiseResult> {
    this.terminate();
    const worker = new Worker(new URL('./denoise-worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker = worker;

    return new Promise<DenoiseResult>((resolve, reject) => {
      worker.onmessage = (ev: MessageEvent<DenoiseWorkerMessage>) => {
        const msg = ev.data;
        switch (msg.type) {
          case 'progress':
            onProgress(msg.total > 0 ? msg.processed / msg.total : 0);
            break;
          case 'done':
            resolve({
              wet: new Float32Array(msg.wet),
              dry: prepared.samples,
              sampleRate: prepared.sampleRate,
              noiseFloorInDb: msg.noiseFloorInDb,
              noiseFloorOutDb: msg.noiseFloorOutDb,
              speechRatio: msg.speechRatio,
            });
            this.terminate();
            break;
          case 'error':
            reject(new Error(msg.message));
            this.terminate();
            break;
          case 'ready':
            break;
        }
      };
      worker.onerror = (e) => {
        reject(new Error(e.message || 'Denoiser worker failed to start'));
        this.terminate();
      };
      // Copy the PCM so the caller keeps its original for A/B playback.
      const copy = prepared.samples.slice();
      worker.postMessage({ type: 'run', pcm: copy.buffer }, [copy.buffer]);
    });
  }

  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}

/** Encode a mono signal to WAV or MP3 via the encode worker. */
export function encodeAudio(
  samples: Float32Array,
  sampleRate: number,
  format: OutputFormat,
  onProgress: (fraction: number) => void,
): Promise<{ blob: Blob; mime: string }> {
  const worker = new Worker(new URL('./encode-worker.ts', import.meta.url), {
    type: 'module',
  });
  return new Promise((resolve, reject) => {
    worker.onmessage = (ev: MessageEvent<EncodeWorkerMessage>) => {
      const msg = ev.data;
      if (msg.type === 'progress') {
        onProgress(msg.ratio);
      } else if (msg.type === 'done') {
        resolve({ blob: new Blob([msg.buffer], { type: msg.mime }), mime: msg.mime });
        worker.terminate();
      } else if (msg.type === 'error') {
        reject(new Error(msg.message));
        worker.terminate();
      }
    };
    worker.onerror = (e) => {
      reject(new Error(e.message || 'Encoder worker failed to start'));
      worker.terminate();
    };
    const copy = samples.slice();
    worker.postMessage(
      { type: 'encode', format, pcm: copy.buffer, sampleRate, kbps: 128 },
      [copy.buffer],
    );
  });
}
